import { AnalyticsEvent } from './analytics-events'
import { analyticsService } from './analytics-service'
import { featuredGuestJourneyService } from './featured-guest-journey-service'
import { logger } from '../util/logger'

/** Where a featured card was played from — the two surfaces convert very differently. */
export type FeaturedSurface = 'home' | 'sign-in'

type WorkoutIdentity = { workoutId: string; workoutTitle: string }

/**
 * Every featured-workouts analytics call, in one place — the web half of the GH-861 contract
 * (resource/docs/plans/featured-workouts-analytics-contract.md). Routing them through here
 * rather than calling `analyticsService.track` from eight components is what keeps `surface`
 * on every event and stops the property names drifting apart one call site at a time.
 *
 * Every call is best-effort: on the sign-in page these fire before the app has bootstrapped
 * anything, and an analytics failure must not surface to a visitor who only wants to watch a
 * workout. Nothing here may gate playback, section visibility or the upsell.
 */
export const featuredAnalytics = {
  /**
   * The denominator. Without it, view→play and view→signup have only a numerator. Never fired
   * for an empty set: an empty set renders nothing, so counting it would inflate the
   * denominator with impressions that never happened.
   */
  shelfViewed: (params: { surface: FeaturedSurface; featuredWorkoutCount: number }): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_SHELF_VIEWED, params)
  },

  playClicked: (params: WorkoutIdentity & { surface: FeaturedSurface; signedIn: boolean }): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_PLAY_CLICKED, params)
  },

  /** A muted hover preview actually began (after the intent delay). Web only — no hover on touch. */
  previewStarted: (params: { workoutId: string; surface: FeaturedSurface }): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_PREVIEW_STARTED, params)
  },

  /**
   * A guest started a free workout. Tracked *and* remembered — the memory is what lets the
   * account they may create carry "trained before signing up".
   */
  guestPlayStarted: ({ workoutId, workoutTitle }: WorkoutIdentity): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_STARTED, {
      workoutId,
      workoutTitle,
      surface: 'sign-in',
      signedIn: false,
    })
    try {
      featuredGuestJourneyService.recordPlay({ workoutId })
    } catch (err) {
      logger.debug('featured guest play not recorded', err)
    }
  },

  /** A guest passed the completion threshold. Same deal. */
  guestPlayCompleted: ({ workoutId, workoutTitle }: WorkoutIdentity): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_COMPLETED, {
      workoutId,
      workoutTitle,
      surface: 'sign-in',
      signedIn: false,
    })
    try {
      featuredGuestJourneyService.recordCompletion()
    } catch (err) {
      logger.debug('featured guest completion not recorded', err)
    }
  },

  /** One row per play, however it ended, so finishing and abandoning are a distribution. */
  ended: (
    params: Partial<WorkoutIdentity> & {
      surface: FeaturedSurface
      signedIn: boolean
      featuredSecondsWatched: number
      featuredPercentWatched: number
      featuredReachedCompletion: boolean
    },
  ): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_ENDED, params)
  },

  /**
   * A guest is handed an unsigned HLS URL with no credentials, so a dead manifest or a 403
   * would otherwise show up only as plays quietly not happening.
   */
  playFailed: (params: { workoutId: string; featuredErrorCode: number | null; featuredStarted: boolean }): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_WORKOUT_PLAY_FAILED, params)
  },

  /**
   * The post-workout prompt. With the paywall auto-opening after the completion modal there is
   * no click to record, so this single event is both the impression and the open; conversion
   * is read off the paywall via its `source`.
   */
  upsellShown: (params: {
    workoutTitle: string
    blockedWorkoutId?: string
    playedFeaturedCount: number
    daysSinceSignUp?: number
  }): void => {
    analyticsService.track(AnalyticsEvent.FEATURED_UPSELL_SHOWN, params)
  },
}
